import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardSummaryStore, $initialCurrenciesAvailableStore, $profileFeatureStore } from '@lib/store';
import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { DashboardSpendingCurrency } from './dashboardSpendingCurrency';
import { DashboardExpenseCategories } from './dashboardExpenseCategories';
import { DashboardMerchants } from './dashboardMerchants';
import { ColorsEnum, NoData, Spinner, Text } from '@shared/widgets';
import styles from './tab.module.scss';

export const CardTab = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const dashboardSummaryStore = useUnit($dashboardSummaryStore.store);
  const currencyByAccountStore = useUnit($initialCurrenciesAvailableStore.store);

  const { t } = useTranslation('dashboard');

  useEffect(() => {
    if (!profileFeatureStore.data?.dashboard.viewCurrenciesSummary) return;
    $dashboardSummaryStore.events.update();
  }, [profileFeatureStore]);

  return (
    <>
      {!profileFeatureStore.data ? (
        <NoData />
      ) : profileFeatureStore.loading || dashboardSummaryStore.loading || currencyByAccountStore.loading ? (
        <Spinner className={cn(PaletteUtils.isVcfcPalette && 'my-10')} />
      ) : (
        dashboardSummaryStore.data.length > 0 && (
          <div
            className={cn(
              'flex flex-col',
              PaletteUtils.isInsigniaPalette && 'gap-6',
              PaletteUtils.isVcfcPalette && 'bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
            )}
          >
            <Text
              className={cn(PaletteUtils.isVcfcPalette && 'mb-4')}
              weight={PaletteUtils.isVcfcPalette ? 'semibold' : 'medium'}
              size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}
            >
              {t('card.currency.title', { Count: PaletteUtils.isInsigniaPalette ? `(${dashboardSummaryStore.data.length})` : '' })}
            </Text>

            {dashboardSummaryStore.data.map((item, idx) => {
              const symbol = CurrencyUtils.getSymbolByCode(item.currency);
              return (
                <div
                  key={idx}
                  className={cn(
                    'flex bg-white',
                    PaletteUtils.isInsigniaPalette && 'pr-12 dark:bg-soft-black',
                    PaletteUtils.isVcfcPalette && 'my-6 items-center dark:bg-light/5',
                  )}
                >
                  <div className='pr-12'>
                    {PaletteUtils.isInsigniaPalette && (
                      <div className={cn(styles['currency__icon'])} data-icon={symbol}>
                        <div
                          className={cn(
                            styles['currency__icon-cut'],
                            'absolute bottom-[-2px] left-[calc(100%-2px)] h-[150%] w-1/2 origin-bottom-left rotate-[-20deg] border-l border-primary bg-white dark:bg-soft-black',
                          )}
                        />
                      </div>
                    )}
                    {PaletteUtils.isVcfcPalette && (
                      <div className='flex h-[72px] w-[72px] items-center justify-center rounded-[100px] bg-primary'>
                        <Text size='3xl' color={ColorsEnum.White}>
                          {symbol}
                        </Text>
                      </div>
                    )}
                  </div>
                  <div className='flex w-full items-center justify-around gap-6 py-4 max-[1100px]:grid max-[1100px]:grid-cols-1 max-[1100px]:gap-3'>
                    <div className='flex flex-col'>
                      <Text size='2xl' className={cn(PaletteUtils.isVcfcPalette && 'font-[NewtonCCT]')}>
                        {item.accounts}
                      </Text>
                      <Text
                        weight={PaletteUtils.isInsigniaPalette ? 'medium' : 'semibold'}
                        color={PaletteUtils.isVcfcPalette ? ColorsEnum.SecondaryLight : ColorsEnum.Secondary}
                      >
                        {t('card.currency.fields.accounts')}
                      </Text>
                    </div>
                    <div className='flex flex-col'>
                      <Text className={cn(PaletteUtils.isVcfcPalette && 'font-[NewtonCCT]')} size='2xl'>
                        {item.cards}
                      </Text>
                      <Text
                        weight={PaletteUtils.isInsigniaPalette ? 'medium' : 'semibold'}
                        color={PaletteUtils.isVcfcPalette ? ColorsEnum.SecondaryLight : ColorsEnum.Secondary}
                      >
                        {t('card.currency.fields.cards')}
                      </Text>
                    </div>
                    <div className='flex flex-col'>
                      <Text size='2xl' className={cn(PaletteUtils.isVcfcPalette && 'font-[NewtonCCT]')}>
                        {AmountUtils.transformToAmountWithCurrency(item.availableBalance, item.currency)}
                      </Text>
                      <Text
                        weight={PaletteUtils.isInsigniaPalette ? 'medium' : 'semibold'}
                        color={PaletteUtils.isVcfcPalette ? ColorsEnum.SecondaryLight : ColorsEnum.Secondary}
                      >
                        {t('card.currency.fields.availableBalance')}
                      </Text>
                    </div>
                    <div className='flex flex-col'>
                      <Text size='2xl' className={cn(PaletteUtils.isVcfcPalette && 'font-[NewtonCCT]')}>
                        {AmountUtils.transformToAmountWithCurrency(item.amountDue, item.currency)}
                      </Text>
                      <Text
                        weight={PaletteUtils.isInsigniaPalette ? 'medium' : 'semibold'}
                        color={PaletteUtils.isVcfcPalette ? ColorsEnum.SecondaryLight : ColorsEnum.Secondary}
                      >
                        {t('card.currency.fields.amountDue')}
                      </Text>
                    </div>
                    <div className='flex flex-col'>
                      <Text size='2xl' className={cn(PaletteUtils.isVcfcPalette && 'font-[NewtonCCT]')}>
                        {AmountUtils.transformToAmountWithCurrency(item.totalLimit, item.currency)}
                      </Text>
                      <Text
                        weight={PaletteUtils.isInsigniaPalette ? 'medium' : 'semibold'}
                        color={PaletteUtils.isVcfcPalette ? ColorsEnum.SecondaryLight : ColorsEnum.Secondary}
                      >
                        {t('card.currency.fields.totalLimit')}
                      </Text>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
      {profileFeatureStore.data?.dashboard.viewSpendingByCurrency && <DashboardSpendingCurrency />}
      {profileFeatureStore.data?.dashboard.viewExpenseCategories && <DashboardExpenseCategories />}
      {profileFeatureStore.data?.dashboard.viewMostUsedMerchants && <DashboardMerchants />}
    </>
  );
};
